import { useGetPagesQuery } from 'api/siteApi.ts';
import { Share2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'ui';
import Button from 'ui/components/Button';
import { cn } from 'utils/cn';
import { normalizeUrl } from 'utils/proxyUtils.ts';
import { useResponsive } from 'utils/useMediaQuery';
import { getSelectedSiteCollaborateInfo, getSelectedSiteInfo } from '../../../store/siteInfoSlice.ts';
import { setPageId } from '../../../store/tasksSlice.ts';
import { getBrowseMode, getIsBottomBarLoading, setShareModalOpen } from '../../../store/uiSlice';
import { ShareModal } from '../../modals/share/share-modal';

export const ShareTask = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo)
  const siteInfo = useSelector(getSelectedSiteInfo)
  const { data: pageData } = useGetPagesQuery({ siteId: collaborateInfo?.result?.id ?? '' });
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);
  const { isMobile } = useResponsive();
  const isInitialLoad = useRef(true);
  const prevBrowseMode = useRef(false);

  const handleShareClick = () => {
    dispatch(setShareModalOpen(true));
  };

  const pageId = useMemo(() => {
    if (!pageData?.data.pages.page) return null;

    try {
      const currentUrl = normalizeUrl(window.location.href);

      // Find the page with a matching URL
      const matchingPage = pageData.data.pages.page.find(page => {
        const pageUrl = normalizeUrl(page.url);
        return pageUrl === currentUrl;
      });
      return matchingPage?.id ?? null;
    } catch (error) {
      console.error('Error finding matching page:', error);
      return null;
    }

  }, [pageData]);

  useEffect(() => {
    if (pageId) {
      dispatch(setPageId(pageId));
    }
  }, [pageId, dispatch]);

  // const isGodaddyGuest = collaborateInfo?.result.godaddy_username || (collaborateInfo?.result.account_id !== undefined && enterpriseClients.includes(collaborateInfo.result.account_id)) || false;
  const browseMode = useSelector(getBrowseMode);

  // Track if this is the initial load vs a mode change
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      prevBrowseMode.current = browseMode;
    } else {
      prevBrowseMode.current = browseMode;
    }
  }, [browseMode]);

  // Determine if we should animate (only on mode changes, not initial load)
  const shouldAnimate = !isInitialLoad.current && prevBrowseMode.current !== browseMode;
  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-[78px]" />
      ) : (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Share2 size={18} />}
          onClick={handleShareClick}
          className={cn(
            'border-primary border w-9 md:w-fit px-1 md:px-2 gap-0 ',
            browseMode ? 'gap-0 md:px-2' : null
          )}
          tooltip={t('bottom_bar.share_task_tooltip', 'Invite others to collaborate and provide feedback')}
          tooltipSide="top"
        // disabled={Boolean(isGodaddyGuest)}
        >
          {!browseMode && !isMobile && (
            <motion.span
              initial={shouldAnimate ? { width: 0, opacity: 0, marginLeft: 0 } : { width: 'auto', opacity: 1, marginLeft: 6 }}
              animate={{ width: 'auto', opacity: 1, marginLeft: 6 }}
              exit={{ width: 0, opacity: 0, marginLeft: 0 }}
              transition={{ duration: 0.4, delay: shouldAnimate ? 0.75 : 0, type: 'spring' }}>
              {t('bottom_bar.share', 'Share')}
            </motion.span>
          )}
        </Button>
      )}
      {collaborateInfo && siteInfo && (<ShareModal siteId={collaborateInfo.result.id} siteRole={collaborateInfo.result?.role} workspaceId={siteInfo.workspace_id ?? 0} />)}
    </>
  );
};
